import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { Close, ExpandMore, Group, SearchOutlined } from "@mui/icons-material";
import { Datepicker, locale, type MbscDatepickerChangeEvent } from "@mobiscroll/react";
import generateDisabledWeekdays from "../../../components/BookingForm/DateTimePicker/functions/generate-disabled-weekdays.function";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import type { RootState } from "../../../app/rootReducer";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { ColleagueBookingFilters } from "../../../components/BookingForm/BookingFilter/ColleagueBookingFilter.component";
import { ColleagueFilterState } from "../../../components/BookingForm/BookingFilter/types/GroupFilters.type";
import { genConnectedBookingData } from "../../../components/BookingForm/BookingFilter/functions/BookingFilterButtonGroup.functions";
import { getUniqueConnectedBookingsForColleagueFilter } from "../../../components/BookingForm/functions/form-workplace-connected-booking.functions";
import type { BookingInputs } from "../../../components/FacilityManager/Domain/Types/BookingInputs.type";
import { CancelExistingBookingsDialog } from "../../../components/BookingForm/cancel-existing-bookings-dialog.component";
import { defaultRecurrenceWeekdays } from "../../Booking-Form/typings/shared/weekdays";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { ConnectedBookingData } from "../../Booking-Form/typings/connected-bookingData";
import { BookingTimeEditFieldDialog } from "../../../components/BookingForm/FormEditTimeDialog/booking-time-edit-field-dialog.component";
import { ScheduleSelectionDialog } from "../../../components/Schedule/ScheduleCalendar/ScheduleSelectionDialog/ScheduleSelectionDialog.component";
import { ScheduleMode } from "../../Schedule/partial/ScheduleModeToggle.partial";
import { ColleagueWorkplaceFloorCanvas } from "./colleague-workplace-floor-canvas.partial";
import { ColleagueFloorConferenceConfirm } from "./colleague-floor-conference-confirm.partial";
import { ColleagueWorkplaceFloorOccupancyTable } from "./colleague-workplace-floor-occupancy-table.partial";
import { useColleagueFloorOccupancy } from "./useColleagueFloorOccupancy.hook";
import {
  colleagueFloorZoneByInventoryId,
  colleagueFloorDisplayHighlightId,
  colleagueFloorInventoryName,
  isColleagueFloorOwnBookingHighlight,
  isColleagueSessionHighlightValid,
  toColleagueFloorDateInputValue,
  toColleagueFloorTimeInputValue
} from "./colleague-workplace-floor.functions";
import { ColleagueFloorLegendItem } from "./colleague-workplace-floor-legend.partial";
import type { ColleagueWorkplaceFloorSession } from "./colleague-workplace-floor.types";
import { useColleagueWorkplaceFloorData } from "./useColleagueWorkplaceFloorData.hook";
import { useColleagueFloorBookingWrites } from "./useColleagueFloorBookingWrites.hook";
import { useRemoteFetchCompanyLocations } from "../../../hooks/Remote/Location/useRemoteFetchCompanyLocations";
import { NO_BOOKING_TIME_LOCATION_NAME } from "../utils/dashboard2-favorite-locations.utils";
import { SEEDIT_FONT_FAMILY_MONO } from "../../../themes/seedit-theme.tokens";

/** Place types whose book prompt stores a favorite of that type. A room uses the sheet. */
const COLLEAGUE_FLOOR_PLACE_PROMPT_TYPES = new Set<BookingType>([
  BookingType.PARKINGPLACE,
  BookingType.ELECTRICCHARGINGSTATIONPLACE
]);

type P = {
  session: ColleagueWorkplaceFloorSession;
  onClose: () => void;
  onBooked: () => void;
  onSessionChange: (next: Partial<ColleagueWorkplaceFloorSession>) => void;
};

type ColleagueFloorLocationOption = {
  id: number;
  name: string;
};

/** Offices the user marked as bookable in booking settings (`preferredLocations` holds location ids). */
function isColleagueFloorSelectableLocation(
  location: { id: number; name: string; isMobileWorking: boolean; allowForBooking: boolean },
  preferredLocationIds: Set<string> | undefined
): boolean {
  if (location.isMobileWorking || location.name === NO_BOOKING_TIME_LOCATION_NAME) {
    return false;
  }
  if (location.allowForBooking === false) {
    return false;
  }
  if (preferredLocationIds != null && !preferredLocationIds.has(String(location.id))) {
    return false;
  }
  return true;
}

function ColleagueFloorLocationField({
  locationName,
  locationInventoryId,
  locations,
  onSelect
}: Readonly<{
  locationName?: string;
  locationInventoryId?: number;
  locations: ColleagueFloorLocationOption[];
  onSelect: (location: ColleagueFloorLocationOption) => void;
}>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const label = locationName || t("_colleagueFloor_noLocation");
  const menuOpen = anchorEl != null;

  return (
    <>
      <Button
        type="button"
        variant="outlined"
        color="inherit"
        disabled={locations.length === 0}
        endIcon={<ExpandMore />}
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={t("_colleagueFloor_changeLocation")}
        data-testid="colleague-workplace-floor-location"
        sx={theme => {
          const frame = getColleagueFloorFrameSx(theme);
          return {
            alignSelf: "stretch",
            height: "auto",
            px: 1.5,
            justifyContent: "space-between",
            width: { xs: "100%", sm: "auto" },
            minWidth: { sm: 160 },
            maxWidth: { sm: 280 },
            ...frame,
            fontFamily: "Fira Sans",
            fontWeight: 700,
            fontSize: "1rem",
            textTransform: "none",
            color: theme.palette.text.primary,
            "&:hover": {
              bgcolor: getColleagueFloorChipColors(theme, false).hoverBackground,
              borderColor: frame.borderColor,
              borderBottom: frame.borderBottom
            }
          };
        }}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {label}
        </Box>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        data-testid="colleague-workplace-floor-location-menu"
      >
        {locations.map(location => (
          <MenuItem
            key={location.id}
            selected={location.id === locationInventoryId}
            onClick={() => {
              setAnchorEl(null);
              if (location.id !== locationInventoryId) {
                onSelect(location);
              }
            }}
            data-testid={`colleague-workplace-floor-location-option-${location.id}`}
          >
            {location.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/** Colors and emphasis for a floor tab in its selected or unselected state. */
type ColleagueFloorChipColors = {
  fontWeight: number;
  text: string;
  background: string;
  border: string;
  shadow: string;
  hoverBackground: string;
  hoverText: string;
  countText: string;
  countBackground: string;
};

function getColleagueFloorChipColors(theme: Theme, selected: boolean): ColleagueFloorChipColors {
  const { primary, text, background } = theme.palette;
  if (selected) {
    return {
      fontWeight: 700,
      text: primary.contrastText,
      background: primary.main,
      border: primary.main,
      shadow: `0 -2px 6px ${alpha(primary.main, 0.25)}`,
      hoverBackground: primary.dark,
      hoverText: primary.contrastText,
      countText: primary.contrastText,
      countBackground: alpha(primary.contrastText, 0.22)
    };
  }
  return {
    fontWeight: 600,
    text: text.secondary,
    background: background.paper,
    border: alpha(primary.main, 0.2),
    shadow: "none",
    hoverBackground: alpha(primary.main, 0.08),
    hoverText: text.primary,
    countText: text.primary,
    countBackground: alpha(primary.main, 0.12)
  };
}

function getColleagueFloorFrameSx(theme: Theme) {
  const colors = getColleagueFloorChipColors(theme, false);
  return {
    borderRadius: "8px 8px 0 0",
    bgcolor: theme.palette.background.default,
    border: "1px solid",
    borderColor: colors.border,
    borderBottom: `3px solid ${alpha(theme.palette.primary.main, 0.35)}`
  };
}

function ColleagueFloorChip({
  floorName,
  colleagueCount,
  selected,
  onClick,
  testId
}: Readonly<{
  floorName: string;
  colleagueCount: number;
  selected: boolean;
  onClick: () => void;
  testId: string;
}>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const label = t("_colleagueFloor_floorChip", { floorName, count: colleagueCount });
  const colors = getColleagueFloorChipColors(theme, selected);
  const chipRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!selected) {
      return;
    }

    const chip = chipRef.current;
    if (typeof chip?.scrollIntoView !== "function") {
      return;
    }

    chip.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [selected]);

  return (
    <Button
      ref={chipRef}
      type="button"
      disableElevation
      onClick={onClick}
      aria-pressed={selected}
      aria-label={label}
      data-testid={testId}
      sx={{
        alignSelf: "stretch",
        flexShrink: 0,
        height: "auto",
        minHeight: 40,
        px: 1.5,
        gap: 0.75,
        borderRadius: "8px 8px 0 0",
        textTransform: "none",
        fontFamily: "Fira Sans",
        fontWeight: colors.fontWeight,
        fontSize: "0.9375rem",
        lineHeight: 1,
        whiteSpace: "nowrap",
        color: colors.text,
        bgcolor: colors.background,
        border: "1px solid",
        borderColor: colors.border,
        borderBottom: "none",
        boxShadow: colors.shadow,
        transition: theme.transitions.create(["background-color", "border-color", "color"], {
          duration: theme.transitions.duration.shorter
        }),
        "&:hover": {
          bgcolor: colors.hoverBackground,
          color: colors.hoverText
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none"
        }
      }}
    >
      {floorName}
      <Box
        component="span"
        aria-hidden
        sx={{
          minWidth: 22,
          height: 22,
          px: 0.75,
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: SEEDIT_FONT_FAMILY_MONO,
          fontWeight: 600,
          fontSize: "0.75rem",
          lineHeight: 1,
          color: colors.countText,
          bgcolor: colors.countBackground
        }}
      >
        {colleagueCount}
      </Box>
    </Button>
  );
}

/** Tooltip translation key per colleague search mode, describing what the next click does. */
const COLLEAGUE_FILTER_TOOLTIP_KEYS: Record<ColleagueFilterState, string> = {
  [ColleagueFilterState.DEACTIVATED]: "_colleagueFloor_colleagueSearchShow",
  [ColleagueFilterState.FILLED]: "_colleagueFloor_colleagueSearchClear",
  [ColleagueFilterState.EMPTY]: "_colleagueFloor_colleagueSearchHide"
};

function getNextColleagueFilterState(state: ColleagueFilterState): ColleagueFilterState {
  switch (state) {
    case ColleagueFilterState.DEACTIVATED:
      return ColleagueFilterState.FILLED;
    case ColleagueFilterState.FILLED:
      return ColleagueFilterState.EMPTY;
    case ColleagueFilterState.EMPTY:
      return ColleagueFilterState.DEACTIVATED;
  }
}

export function ColleagueWorkplaceFloorDialog({
  session,
  onClose,
  onBooked,
  onSessionChange
}: Readonly<P>) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { dateFormat, timeFormat } = useTimeFormat();
  const {
    userInformation: { sub, firstName, surname, photoUrl, email, company },
    settings
  } = useSelector((state: RootState) => state.login);
  const disabledWeekdays = useMemo(
    () =>
      generateDisabledWeekdays({
        bookingsOnSaturdaysAllowed: company?.meta?.bookingsOnSaturdaysAllowed,
        bookingsOnSundaysAllowed: company?.meta?.bookingsOnSundaysAllowed
      }),
    [company?.meta?.bookingsOnSaturdaysAllowed, company?.meta?.bookingsOnSundaysAllowed]
  );
  const preferredLocations: string[] | undefined = settings?.preferredLocations;
  const preferredLocationIds = useMemo(
    () => (preferredLocations == null ? undefined : new Set(preferredLocations)),
    [preferredLocations]
  );

  const user = useMemo(
    () => ({ userId: sub, firstName, surname, photoUrl, email }),
    [email, firstName, photoUrl, sub, surname]
  );
  const [selectedFloorId, setSelectedFloorId] = useState<number | undefined>();
  /** `null` means "not edited by the user": show every colleague booked on the current floor. */
  const [pickedColleagues, setPickedColleagues] = useState<ConnectedBookingData[] | null>(null);
  const [colleagueFilterState, setColleagueFilterState] = useState(ColleagueFilterState.FILLED);
  const [isSessionTimeEditorOpen, setIsSessionTimeEditorOpen] = useState(false);
  const [conferenceInventoryId, setConferenceInventoryId] = useState<number | undefined>();
  const colleagueFilterTooltip = t(COLLEAGUE_FILTER_TOOLTIP_KEYS[colleagueFilterState]);
  const bookingTimezone = DateTime.local().zoneName;

  const { data: companyLocations } = useRemoteFetchCompanyLocations({
    isFinanceRequest: false,
    noMobileWorking: true,
    isBookingSettings: true,
    enabled: session.open
  });
  const locationOptions = useMemo(() => {
    const fromApi = (companyLocations ?? [])
      .filter(location => isColleagueFloorSelectableLocation(location, preferredLocationIds))
      .map(location => ({ id: location.id, name: location.name }));
    const currentLocationName = session.locationName;
    const currentIsSelectable =
      session.locationInventoryId != null &&
      currentLocationName != null &&
      isColleagueFloorSelectableLocation(
        {
          id: session.locationInventoryId,
          name: currentLocationName,
          isMobileWorking: false,
          allowForBooking: true
        },
        preferredLocationIds
      );
    if (
      currentIsSelectable &&
      session.locationInventoryId != null &&
      currentLocationName != null &&
      !fromApi.some(location => location.id === session.locationInventoryId)
    ) {
      return [{ id: session.locationInventoryId, name: currentLocationName }, ...fromApi];
    }
    return fromApi;
  }, [companyLocations, preferredLocationIds, session.locationInventoryId, session.locationName]);

  const floorData = useColleagueWorkplaceFloorData({
    enabled: session.open,
    locationInventoryId: session.locationInventoryId,
    date: session.date,
    startTime: session.startTime,
    endTime: session.endTime,
    user,
    selectedFloorInventoryId: selectedFloorId,
    highlightInventoryId: session.highlightInventoryId,
    availabilityBookingType: session.availabilityBookingType,
    preferredFloorId: session.preferredFloorId,
    preferredFloorName: session.preferredFloorName
  });
  const occupancy = useColleagueFloorOccupancy({
    date: session.date,
    currentFloor: floorData.currentFloor,
    currentUser: {
      userId: sub,
      firstName,
      lastName: surname,
      email
    },
    refetchFloor: floorData.refetch
  });

  useEffect(() => {
    setSelectedFloorId(undefined);
    setPickedColleagues(null);
    setColleagueFilterState(ColleagueFilterState.FILLED);
    setConferenceInventoryId(undefined);
  }, [session.date, session.startTime, session.endTime, session.locationInventoryId]);

  useEffect(() => {
    if (
      !session.open ||
      session.highlightInventoryId == null ||
      floorData.isLoading ||
      floorData.floorInventory.length === 0
    ) {
      return;
    }

    if (
      isColleagueFloorOwnBookingHighlight(
        session.highlightInventoryId,
        session.occupantInventoryIds
      )
    ) {
      return;
    }

    if (
      !isColleagueSessionHighlightValid(floorData.floorInventory, session.highlightInventoryId, sub)
    ) {
      onSessionChange({ highlightInventoryId: undefined });
    }
  }, [
    floorData.floorInventory,
    floorData.isLoading,
    onSessionChange,
    session.highlightInventoryId,
    session.occupantInventoryIds,
    session.open,
    sub
  ]);

  const displayHighlightInventoryId = useMemo(
    () =>
      colleagueFloorDisplayHighlightId({
        highlightInventoryId: session.highlightInventoryId,
        floors: floorData.floorInventory,
        isLoading: floorData.isLoading
      }),
    [floorData.floorInventory, floorData.isLoading, session.highlightInventoryId]
  );

  useEffect(() => {
    if (selectedFloorId == null && floorData.resolvedFloorId != null) {
      setSelectedFloorId(floorData.resolvedFloorId);
    }
  }, [floorData.resolvedFloorId, selectedFloorId]);

  const colleagueOptions = useMemo(
    () => genConnectedBookingData(floorData.currentFloor?.connectedBookingData ?? []),
    [floorData.currentFloor?.connectedBookingData]
  );
  const searchedColleagues = useMemo(() => {
    const defaultColleagues =
      colleagueFilterState === ColleagueFilterState.FILLED ? colleagueOptions : [];
    return pickedColleagues ?? defaultColleagues;
  }, [colleagueFilterState, colleagueOptions, pickedColleagues]);
  const selectedColleagueBookings = useMemo(
    () => getUniqueConnectedBookingsForColleagueFilter(searchedColleagues, floorData.currentFloor),
    [floorData.currentFloor, searchedColleagues]
  );

  const promptBookingType =
    session.availabilityBookingType != null &&
    COLLEAGUE_FLOOR_PLACE_PROMPT_TYPES.has(session.availabilityBookingType)
      ? session.availabilityBookingType
      : BookingType.WORKPLACE;

  const bookingInputs = useMemo<BookingInputs>(
    () => ({
      mode: promptBookingType,
      usersBookedFor: [{ userId: sub, firstName, surname, photoUrl, isExternal: false }],
      bookingFrom: session.date,
      bookingTo: session.date,
      bookingStart: session.startTime,
      bookingEnd: session.endTime,
      timezone: bookingTimezone,
      weekdays: defaultRecurrenceWeekdays,
      bookingType: promptBookingType,
      zoneAccess: null,
      selectedLocation: session.locationInventoryId,
      selectedFloor: selectedFloorId
    }),
    [
      firstName,
      promptBookingType,
      selectedFloorId,
      session.date,
      session.endTime,
      session.locationInventoryId,
      session.startTime,
      bookingTimezone,
      photoUrl,
      sub,
      surname
    ]
  );

  const handleOpenSessionTimeEditor = useCallback(() => {
    setIsSessionTimeEditorOpen(true);
  }, []);

  const handleSessionTimeEditorChange = useCallback(
    (time: { bookingStart?: string; bookingEnd?: string }) => {
      if (time.bookingStart && time.bookingEnd) {
        onSessionChange({ startTime: time.bookingStart, endTime: time.bookingEnd });
      }
    },
    [onSessionChange]
  );

  const handleLocationSelect = useCallback(
    (location: ColleagueFloorLocationOption) => {
      onSessionChange({
        locationInventoryId: location.id,
        locationName: location.name,
        highlightInventoryId: undefined,
        preferredFloorId: undefined,
        preferredFloorName: undefined
      });
    },
    [onSessionChange]
  );

  useEffect(() => {
    if (preferredLocationIds == null || locationOptions.length === 0) {
      return;
    }
    const currentIsListed = locationOptions.some(
      location => location.id === session.locationInventoryId
    );
    if (currentIsListed) {
      return;
    }
    handleLocationSelect(locationOptions[0]);
  }, [handleLocationSelect, locationOptions, preferredLocationIds, session.locationInventoryId]);

  const bookingWrites = useColleagueFloorBookingWrites({
    session,
    user,
    bookingTimezone,
    onBooked
  });

  const conferenceZone = useMemo(
    () => colleagueFloorZoneByInventoryId(floorData.floorPlan?.zones, conferenceInventoryId),
    [conferenceInventoryId, floorData.floorPlan?.zones]
  );

  const blocksFloorClose = occupancy.blocksParentClose || bookingWrites.blocksParentClose;

  const mobiscrollControlSx = {
    "& .mbsc-material.mbsc-textfield-wrapper": { m: 0 },
    "& .mbsc-textfield-wrapper": {
      width: "100%",
      minHeight: "36px !important"
    },
    "& .mbsc-textfield-inner": {
      height: "36px !important",
      minHeight: "36px !important"
    },
    "& .mbsc-textfield": {
      height: "36px !important",
      minHeight: "36px !important",
      py: "0 !important"
    }
  };

  const dateTimeLabelSx = {
    display: "block",
    mb: 0.25,
    color: "text.secondary",
    fontSize: "0.6875rem",
    fontWeight: 700,
    lineHeight: 1
  };

  const dateTimeControls = (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="nowrap"
      data-testid="colleague-workplace-floor-datetime"
      sx={theme => ({
        px: 0.75,
        py: 0.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.action.hover
      })}
    >
      <Box
        data-testid="colleague-workplace-floor-date-field"
        sx={[
          {
            flex: { xs: "1.4 1 0", sm: "0 0 auto", md: "1.4 1 0", lg: "0 0 auto" },
            width: { sm: 178, md: "auto", lg: 178 },
            minWidth: { xs: 0, md: 112, lg: 0 }
          },
          mobiscrollControlSx
        ]}
      >
        <Typography variant="caption" sx={dateTimeLabelSx}>
          {t("_colleagueFloor_date")}
        </Typography>
        <Datepicker
          controls={["calendar"]}
          data-testid="colleague-workplace-floor-date"
          inputProps={{
            "aria-label": t("_colleagueFloor_date"),
            "data-testid": "colleague-workplace-floor-date"
          }}
          inputStyle="outline"
          invalid={disabledWeekdays}
          value={session.date}
          dateFormat={dateFormat.toUpperCase()}
          returnFormat="iso8601"
          locale={locale[i18n.language]}
          touchUi={fullScreen}
          onChange={(event: MbscDatepickerChangeEvent) => {
            const date = toColleagueFloorDateInputValue(event.value);
            if (date && date !== session.date) {
              onSessionChange({ date });
            }
          }}
        />
      </Box>
      <Box
        data-testid="colleague-workplace-floor-start-field"
        sx={[
          {
            flex: { xs: "1 1 0", sm: "0 0 auto", md: "1 1 0", lg: "0 0 auto" },
            width: { sm: 132, md: "auto", lg: 132 },
            minWidth: { xs: 0, md: 72, lg: 0 }
          },
          mobiscrollControlSx
        ]}
      >
        <Typography variant="caption" sx={dateTimeLabelSx}>
          {t("_colleagueFloor_from")}
        </Typography>
        <Datepicker
          controls={["time"]}
          data-testid="colleague-workplace-floor-start"
          inputProps={{
            "aria-label": t("_colleagueFloor_from"),
            "data-testid": "colleague-workplace-floor-start"
          }}
          inputStyle="outline"
          value={session.startTime}
          timeFormat={timeFormat}
          stepMinute={15}
          locale={locale[i18n.language]}
          touchUi={fullScreen}
          onChange={(event: MbscDatepickerChangeEvent) => {
            const startTime = toColleagueFloorTimeInputValue(event.value);
            if (startTime && startTime !== session.startTime) {
              onSessionChange({ startTime });
            }
          }}
        />
      </Box>
      <Box
        data-testid="colleague-workplace-floor-end-field"
        sx={[
          {
            flex: { xs: "1 1 0", sm: "0 0 auto", md: "1 1 0", lg: "0 0 auto" },
            width: { sm: 132, md: "auto", lg: 132 },
            minWidth: { xs: 0, md: 72, lg: 0 }
          },
          mobiscrollControlSx
        ]}
      >
        <Typography variant="caption" sx={dateTimeLabelSx}>
          {t("_colleagueFloor_until")}
        </Typography>
        <Datepicker
          controls={["time"]}
          data-testid="colleague-workplace-floor-end"
          inputProps={{
            "aria-label": t("_colleagueFloor_until"),
            "data-testid": "colleague-workplace-floor-end"
          }}
          inputStyle="outline"
          value={session.endTime}
          timeFormat={timeFormat}
          stepMinute={15}
          locale={locale[i18n.language]}
          touchUi={fullScreen}
          onChange={(event: MbscDatepickerChangeEvent) => {
            const endTime = toColleagueFloorTimeInputValue(event.value);
            if (endTime && endTime !== session.endTime) {
              onSessionChange({ endTime });
            }
          }}
        />
      </Box>
    </Stack>
  );

  return (
    <Dialog
      open={session.open}
      onClose={() => {
        if (blocksFloorClose) {
          return;
        }
        onClose();
      }}
      fullScreen={fullScreen}
      maxWidth={false}
      fullWidth
      data-testid="colleague-workplace-floor-dialog"
      sx={{
        "& .MuiDialog-paper": {
          width: { sm: "min(1400px, 96vw)" },
          maxWidth: "96vw",
          height: { sm: "90vh" },
          maxHeight: "96vh",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          position: "relative",
          pr: 6,
          py: 1.5,
          flexShrink: 0
        }}
      >
        <Typography component="span" variant="h6" sx={{ flex: 1, fontFamily: "Fira Sans" }}>
          {t("_colleagueFloor_title")}
        </Typography>
        <IconButton
          onClick={() => {
            if (blocksFloorClose) {
              return;
            }
            onClose();
          }}
          aria-label={t("Close")}
          data-testid="colleague-workplace-floor-close"
          sx={{
            position: "absolute",
            top: "50%",
            right: 8,
            transform: "translateY(-50%)"
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flex: 1,
          minHeight: 0,
          overflow: "hidden"
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems="stretch">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems="stretch"
            sx={{ flex: 1, minWidth: 0 }}
          >
            <ColleagueFloorLocationField
              locationName={session.locationName}
              locationInventoryId={session.locationInventoryId}
              locations={locationOptions}
              onSelect={handleLocationSelect}
            />
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="nowrap"
              alignItems="center"
              data-testid="colleague-workplace-floor-chips"
              sx={theme => ({
                flex: 1,
                minWidth: 0,
                overflowX: "auto",
                px: 0.75,
                pt: 0.75,
                pb: 0.5,
                ...getColleagueFloorFrameSx(theme)
              })}
            >
              {floorData.floorChips.map(chip => (
                <ColleagueFloorChip
                  key={chip.id}
                  floorName={chip.name}
                  colleagueCount={chip.colleagueCount}
                  selected={chip.id === floorData.currentFloor?.id}
                  onClick={() => {
                    setSelectedFloorId(chip.id);
                    setPickedColleagues(null);
                  }}
                  testId={`colleague-workplace-floor-chip-${chip.id}`}
                />
              ))}
            </Stack>
          </Stack>
          <Box
            data-testid="colleague-workplace-floor-datetime-slot"
            sx={{
              flexShrink: { xs: 0, md: 1, lg: 0 },
              width: { xs: "100%", md: "auto" },
              minWidth: { md: 260, lg: 0 },
              maxWidth: { md: 320, lg: "none" }
            }}
          >
            {dateTimeControls}
          </Box>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={theme => ({
            px: 1.25,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.default
          })}
        >
          <Tooltip title={<Typography>{colleagueFilterTooltip}</Typography>}>
            <span>
              <Button
                data-testid="find-colleagues-btn"
                aria-label={colleagueFilterTooltip}
                sx={{ height: 40 }}
                onClick={() => {
                  const nextState = getNextColleagueFilterState(colleagueFilterState);
                  setColleagueFilterState(nextState);
                  if (nextState === ColleagueFilterState.FILLED) {
                    setPickedColleagues(null);
                  }
                }}
                color={
                  colleagueFilterState === ColleagueFilterState.DEACTIVATED ? "error" : "primary"
                }
              >
                {colleagueFilterState === ColleagueFilterState.DEACTIVATED ? (
                  <Group />
                ) : (
                  <SearchOutlined />
                )}
                <Typography sx={{ pl: 1 }}>{colleagueOptions.length}</Typography>
              </Button>
            </span>
          </Tooltip>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              "& .MuiInputBase-root": {
                minHeight: 40,
                alignItems: "center"
              },
              "& .MuiAutocomplete-input": {
                py: "8px !important"
              }
            }}
          >
            {colleagueFilterState !== ColleagueFilterState.DEACTIVATED && (
              <ColleagueBookingFilters
                connectedBookingData={colleagueOptions}
                selectedColleagueFilter={searchedColleagues}
                setSelectedColleagueFilter={setPickedColleagues}
                colleagueFilterState={colleagueFilterState}
                selectAllOnMount={false}
              />
            )}
          </Box>
        </Stack>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            position: "relative"
          }}
        >
          <ColleagueWorkplaceFloorCanvas
            key={session.locationInventoryId}
            floorPlan={floorData.floorPlan}
            currentFloor={floorData.currentFloor}
            isLoading={floorData.isLoading}
            highlightInventoryId={displayHighlightInventoryId}
            occupantInventoryIds={session.occupantInventoryIds}
            ownOccupancyBookings={session.ownOccupancyBookings}
            searchedColleagues={selectedColleagueBookings}
            bookingInputs={bookingInputs}
            timeFormat={timeFormat}
            favoriteBookingType={session.availabilityBookingType ?? BookingType.WORKPLACE}
            availabilityBookingType={session.availabilityBookingType}
            onConfirmInventoryId={bookingWrites.confirmInventory}
            onOpenConferenceRoom={setConferenceInventoryId}
            onOpenSessionTimeEditor={handleOpenSessionTimeEditor}
            onOccupancyInventoryId={inventoryId => {
              occupancy.handleOccupancyInventoryId(inventoryId).catch(() => undefined);
            }}
          />
          {conferenceZone != null &&
            floorData.floorPlan != null &&
            session.locationInventoryId != null && (
              <ColleagueFloorConferenceConfirm
                zone={conferenceZone}
                floorPlan={floorData.floorPlan}
                whenLabel={`${session.date}, ${session.startTime}–${session.endTime}`}
                locationInventoryId={session.locationInventoryId}
                locationName={session.locationName}
                date={session.date}
                startTime={session.startTime}
                endTime={session.endTime}
                timezone={bookingTimezone}
                isSubmitting={bookingWrites.isConferenceSubmitting}
                onCancel={() => {
                  if (!bookingWrites.isConferenceSubmitting) {
                    setConferenceInventoryId(undefined);
                  }
                }}
                onConfirm={bookingWrites.confirmConference}
              />
            )}
        </Box>
        <Stack
          direction="row"
          spacing={2}
          useFlexGap
          flexWrap="wrap"
          sx={theme => ({
            fontFamily: "Fira Sans",
            px: 1,
            py: 0.75,
            borderRadius: 2,
            bgcolor: theme.palette.action.hover
          })}
        >
          <ColleagueFloorLegendItem label={t("_colleagueFloor_legendFree")} marker="free" />
          <ColleagueFloorLegendItem label={t("_colleagueFloor_legendPartial")} marker="partial" />
          <ColleagueFloorLegendItem label={t("_colleagueFloor_legendOccupied")} marker="occupied" />
        </Stack>
      </DialogContent>
      <BookingTimeEditFieldDialog
        open={isSessionTimeEditorOpen}
        setOpen={setIsSessionTimeEditorOpen}
        tempTime={{ bookingStart: session.startTime, bookingEnd: session.endTime }}
        handleChange={handleSessionTimeEditorChange}
        timezone={bookingTimezone}
      />
      <ColleagueWorkplaceFloorOccupancyTable
        open={occupancy.occupancyTableOpen}
        inventoryName={colleagueFloorInventoryName(
          floorData.floorPlan,
          occupancy.occupancyInventoryId
        )}
        bookings={occupancy.occupancyBookings}
        timeFormat={timeFormat}
        isOpeningBooking={occupancy.isOpeningBooking}
        onClose={occupancy.closeOccupancyTable}
        onOpenBooking={booking => {
          occupancy.openScheduleForBooking(booking).catch(() => undefined);
        }}
      />
      {occupancy.selectedSchedule && (
        <ScheduleSelectionDialog
          scheduleMode={ScheduleMode.Colleague}
          isCalendarView
          isEditable
          isOpen
          onClose={occupancy.closeSchedule}
          schedule={occupancy.selectedSchedule}
          schedulePictures={[]}
          pictureIsLoading={false}
          allSchedules={[occupancy.selectedSchedule]}
          calendarData={[]}
          isFirstAidSelected={false}
          refetchAllSchedule={occupancy.refetchAfterScheduleChange}
        />
      )}
      <CancelExistingBookingsDialog
        open={bookingWrites.showCancelExistingConfirm}
        affectedBookings={bookingWrites.affectedExistingBookings}
        confirming={bookingWrites.isConfirmingCancelExisting}
        onProceed={bookingWrites.proceedCancelExisting}
        onDismiss={bookingWrites.dismissCancelExisting}
      />
    </Dialog>
  );
}
